import React from 'react';
import { truncate, upperFirst } from 'lodash';
import moment from 'moment';
import { ELSButton } from '@els/els-component-button-react';
import {
  ELSDate,
  ELSDropDown,
  ELSIcon,
  ELSPaginator,
  ELSTextBox,
  ELSTooltip,
  validateAfterDate,
  validateBeforeDate,
} from '../../components/els.components';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { LibrarySortDto } from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { DATE_PRIMARY } from '../../constants/date.constants';
import { ELSButtonType } from '../../models/button.models';
import { LIBRARY_SORT_OPTIONS } from './library-page.constants';
import {
  getSegmentOptions,
  getInvalidDateValidator,
  getSegmentSizeOptions,
  INVALID_DATE_VALUE,
  isInvalidDateInput,
  isValidDate,
  LIBRARY_SELECTION_RESET,
} from './library-page.utilities';
import { LibraryPage, LibraryRowDto } from './useLibraryPage.hook';

// The words and names that differ between the library pages
export type LibraryPageLabels = {
  // BEM block of the page, e.g. 'c-naa-quiz-questions'
  block: string;
  // Prefix of the form field names, e.g. 'quizQuestions'
  fieldName: string;
  // Page heading, e.g. 'Quiz questions'; also names the table, its pages and the empty states
  title: string;
  // One row, e.g. 'Quiz question', naming its options menu
  item: string;
  // The column header, e.g. 'Question'
  column: string;
  // The items in running text, e.g. 'questions'
  items: string;
  // e.g. 'quiz-question-menu-tooltip', suffixed with the row id
  menuId: string;
  // e.g. 'quiz-questions-paginator'
  paginatorId: string;
};

export type LibraryPageContentProps<T extends LibraryRowDto> = {
  library: LibraryPage<T>;
  labels: LibraryPageLabels;
  // The page's View (and Take) buttons, shown after Clear filters
  actions: React.ReactNode;
};

type FilterPillProps = {
  block: string;
  label: string;
  title: string;
  closeLabel: string;
  onClose: () => void;
};

const FilterPill = (props: FilterPillProps): JSX.Element => (
  <FlexLayout
    classes={[`c-els-pill ${props.block}__filter-pill`]}
    modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.MIDDLE]}
  >
    <FlexItem>
      <span className={`${props.block}__chat-title`} title={props.title}>
        {props.label}
      </span>
    </FlexItem>
    <FlexItem>
      <button
        type="button"
        className={`${props.block}__filter-pill-close-button u-els-anchorize u-els-debuttonize`}
        onClick={props.onClose}
      >
        <ELSIcon name="close" size="1o2" />
        <span className="u-els-hide-visually">{props.closeLabel}</span>
      </button>
    </FlexItem>
  </FlexLayout>
);

// The header, controls, list and paginator shared by the library pages (Quiz questions, Flashcards)
export const LibraryPageContent = <T extends LibraryRowDto>({ library, labels, actions }: LibraryPageContentProps<T>): JSX.Element => {
  const { state, mergeState, response } = library;
  const { block } = labels;
  const isDateRangeSet = !!state.createdFrom && !!state.createdTo;

  const renderMenuAction = (
    row: T,
    ariaLabel: string,
    label: string,
    handleClick: (row: T) => void
  ): JSX.Element => (
    <div className="u-els-margin-bottom-1o2">
      <button
        type="button"
        className="u-els-anchorize"
        aria-label={ariaLabel}
        onClick={() => {
          mergeState({ openMenuRowId: null });
          handleClick(row);
        }}
      >
        {label}
      </button>
    </div>
  );

  const renderRowMenu = (row: T): JSX.Element => (
    <ELSTooltip
      id={`${labels.menuId}-${row.id}`}
      showClose={false}
      hide={state.openMenuRowId !== row.id}
      onOpenToolTip={() => library.openMenu(row)}
      onCloseToolTip={() => library.closeMenu(row)}
      triggerAriaLabel={`Options for ${labels.column.toLowerCase()}: ${truncate(library.rowTextById.get(row.id), { length: 60 })}`}
      dialogAriaLabel={`${labels.item} options menu`}
      content={(
        <div>
          {renderMenuAction(row, `Go to chat: ${row.chatTitle || 'Untitled chat'}`, 'Go to chat', library.handleOpenChatClick)}
          {renderMenuAction(
            row,
            `Filter to all ${labels.items} generated in this chat thread: ${row.chatTitle || 'Untitled chat'}`,
            `Filter to all ${labels.items} generated in this chat thread`,
            library.handleChatTitleClick
          )}
          {renderMenuAction(
            row,
            `Filter to all ${labels.items} generated in this message${row.entryText ? `: ${row.entryText}` : ''}`,
            `Filter to all ${labels.items} generated in this message`,
            library.handleEntryTextClick
          )}
        </div>
      )}
    >
      <span className="c-naa-action-menu__ellipsis">
        <ELSIcon size="1x1o4" prefix="gizmo" name="ellipsis" customClass="u-els-display-block" />
      </span>
    </ELSTooltip>
  );

  const renderRows = (): JSX.Element => (
    <table className="c-els-table c-els-table--padding-1o2" aria-label={labels.title}>
      <thead>
        <tr className="c-els-table__row">
          <th className="c-els-table__cell c-els-table__cell--head u-els-width-9o10">{labels.column}</th>
          <th className="c-els-table__cell c-els-table__cell--head u-els-width-1o10">
            <span className="u-els-hide-visually">Options</span>
          </th>
        </tr>
      </thead>
      <tbody className="c-els-table__body">
        {response.content.map((row) => (
          <tr key={row.id} className="c-els-table__row">
            <td className="c-els-table__cell">
              <div className={`${block}__prompt`}>
                {library.rowTextById.get(row.id)}
              </div>
              <div>
                <small className="u-els-color-n8">{moment(row.createdAt).format(DATE_PRIMARY)}</small>
              </div>
            </td>
            <td className={`c-els-table__cell ${block}__row-menu`}>
              {renderRowMenu(row)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderResults = (): JSX.Element => {
    // `response` is already null while the results do not match the current selection
    if (!response) {
      return null;
    }
    if (!response.totalItems) {
      const itemsText = labels.title.toLowerCase();
      return library.hasFilters
        ? (
          <>
            <p>{`No ${itemsText} match your filters.`}</p>
            <ELSButton preventAutoPending type={ELSButtonType.LINK} onClick={library.clearFilters}>Clear filters</ELSButton>
          </>
        )
        : <p>{`No ${itemsText} yet. Go to a chat and ask for ${itemsText} to be generated, and they will show up here.`}</p>;
    }
    return renderRows();
  };

  const renderPaginator = (): JSX.Element => {
    if (!response?.totalItems) {
      return null;
    }
    return (
      <div className={`${block}__pagination u-els-margin-top`}>
        <ELSPaginator
          id={labels.paginatorId}
          a11y={{
            name: `${labels.title} pages`,
            pagingUnitName: 'Page',
            morePagingUnit: 'More pages',
          }}
          currentPage={state.page + 1}
          totalResults={response.totalItems}
          resultsPerPage={library.pageSize}
          onPageChange={(newPage: number) => mergeState({ page: newPage - 1 })}
        />
      </div>
    );
  };

  const renderFiltersToggle = (): JSX.Element => (
    <div className={`${block}__toggle-wrapper`}>
      <ELSButton
        preventAutoPending
        type={ELSButtonType.LINK}
        ariaExpanded={state.isFiltersOpen}
        onClick={library.toggleFilters}
      >
        <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_1o2, FlexLayoutModifier.MIDDLE]}>
          <FlexItem>
            <ELSIcon name="filter" prefix="gizmo" size="1x" customClass="u-els-display-block" />
          </FlexItem>
          <FlexItem>
            {state.isFiltersOpen ? 'Hide filters' : 'Show filters'}
            {!state.isFiltersOpen && library.hiddenFilterCount > 0 && (
              <span className="u-els-hide-visually">{` (${library.hiddenFilterCount} applied)`}</span>
            )}
          </FlexItem>
        </FlexLayout>
      </ELSButton>
      {!state.isFiltersOpen && library.hiddenFilterCount > 0 && (
        <span className={`${block}__toggle-badge`} aria-hidden="true">{library.hiddenFilterCount}</span>
      )}
    </div>
  );

  const renderFilters = (): JSX.Element => (
    <div className={`${block}__actions`}>
      <FlexLayout classes={[`${block}__fields`]} modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.LEFT, FlexLayoutModifier.WRAP]}>
        {library.hasBookOptions && (
          <FlexItem classes={[`${block}__field--grow ${block}__field--full`]}>
            <ELSDropDown
              name={`${labels.fieldName}Book`}
              value={state.isbn}
              options={library.bookOptions}
              changeHandler={(e, isbn: string) => mergeState({ isbn, ...LIBRARY_SELECTION_RESET })}
            >
              Book
            </ELSDropDown>
          </FlexItem>
        )}
        <FlexItem classes={[`${block}__field--grow ${block}__field--half`]}>
          <ELSDate
            key={`from-${state.datesKey}`}
            name={`${labels.fieldName}CreatedFrom`}
            value={state.isCreatedFromInvalid ? INVALID_DATE_VALUE : state.createdFrom}
            disableAfter={state.createdTo}
            changeHandler={(e, date: Date, details) => mergeState({
              createdFrom: isValidDate(date) ? date : null,
              isCreatedFromInvalid: isInvalidDateInput(date, details),
              ...LIBRARY_SELECTION_RESET
            })}
            validators={[
              getInvalidDateValidator(state.isCreatedFromInvalid),
              ...(isDateRangeSet ? [
                validateBeforeDate(state.createdFrom, moment(state.createdTo).add(1, 'day'), {
                  message: 'Start date must be on or before the end date'
                })
              ] : [])
            ]}
          >
            From
          </ELSDate>
        </FlexItem>
        <FlexItem classes={[`${block}__field--grow ${block}__field--half`]}>
          <ELSDate
            key={`to-${state.datesKey}`}
            name={`${labels.fieldName}CreatedTo`}
            value={state.isCreatedToInvalid ? INVALID_DATE_VALUE : state.createdTo}
            disableBefore={state.createdFrom}
            changeHandler={(e, date: Date, details) => mergeState({
              createdTo: isValidDate(date) ? date : null,
              isCreatedToInvalid: isInvalidDateInput(date, details),
              ...LIBRARY_SELECTION_RESET
            })}
            validators={[
              getInvalidDateValidator(state.isCreatedToInvalid),
              ...(isDateRangeSet ? [
                validateAfterDate(state.createdTo, moment(state.createdFrom).subtract(1, 'day'), {
                  message: 'End date must be on or after the start date'
                })
              ] : [])
            ]}
          >
            To
          </ELSDate>
        </FlexItem>
        <FlexItem classes={[`${block}__field--grow ${block}__field--half`]}>
          <ELSDropDown
            name={`${labels.fieldName}SegmentSize`}
            value={state.segmentSize}
            isDisabled={!library.isResultsCurrent}
            options={getSegmentSizeOptions(library.totalMatches, labels.items)}
            changeHandler={(e, segmentSize: string) => library.handleSegmentSizeChange(segmentSize)}
          >
            {`Number of ${labels.items}`}
          </ELSDropDown>
        </FlexItem>
        {library.isSegmentSized && (
          <FlexItem classes={[`${block}__field--grow ${block}__field--half`]}>
            <ELSDropDown
              name={`${labels.fieldName}Segment`}
              value={state.segment}
              isDisabled={!library.isResultsCurrent}
              options={getSegmentOptions(library.totalMatches, Number(state.segmentSize))}
              changeHandler={(e, segment: string) => mergeState({ segment, page: 0 })}
            >
              {`${upperFirst(labels.items)} to include`}
            </ELSDropDown>
          </FlexItem>
        )}
      </FlexLayout>
    </div>
  );

  return (
    <div className={`c-naa-base-page ${block}`}>
      <div className="c-naa-base-page__header">
        <div className="c-naa-base-page__inner">
          <h3>{labels.title}</h3>
        </div>
      </div>
      <div ref={library.bodyRef} className={`c-naa-base-page__body ${block}__body`}>
        <div className={`c-naa-base-page__inner ${block}__inner`}>

          <div className={`${block}__controls`}>
            <div>
              <FlexLayout
                classes={[`${block}__fields`]}
                modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.LEFT, FlexLayoutModifier.BOTTOM]}
              >
                <FlexItem classes={[`u-els-width-1o4 ${block}__field--half`]}>
                  <ELSTextBox
                    name={`${labels.fieldName}Search`}
                    value={state.searchInput}
                    changeHandler={(e, searchInput: string) => mergeState({ searchInput })}
                  >
                    {`Search ${labels.items}`}
                  </ELSTextBox>
                </FlexItem>
                <FlexItem classes={[`u-els-width-1o4 ${block}__field--half`]}>
                  <ELSDropDown
                    name={`${labels.fieldName}Sort`}
                    value={state.sort}
                    options={LIBRARY_SORT_OPTIONS}
                    changeHandler={(e, sort: LibrarySortDto) => mergeState({ sort, page: 0 })}
                  >
                    Sort
                  </ELSDropDown>
                </FlexItem>
                <FlexItem classes={[`${block}__buttons`]} modifiers={[FlexLayoutModifier.GROW]}>
                  <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.MIDDLE, FlexLayoutModifier.RIGHT]}>
                    <FlexItem>
                      {renderFiltersToggle()}
                    </FlexItem>
                    {library.canClearFilters && (
                      <FlexItem>
                        <ELSButton preventAutoPending type={ELSButtonType.LINK} onClick={library.clearFilters}>
                          Clear filters
                        </ELSButton>
                      </FlexItem>
                    )}
                    {actions}
                  </FlexLayout>
                </FlexItem>
              </FlexLayout>
            </div>

            {state.isFiltersOpen && renderFilters()}

            {(state.chatFilter || state.entryFilter) && (
              <div className="u-els-margin-top">
                <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.MIDDLE, FlexLayoutModifier.WRAP]}>
                  {state.chatFilter && (
                    <FilterPill
                      block={block}
                      label={`Chat: ${state.chatFilter.title || 'Untitled chat'}`}
                      title={state.chatFilter.title}
                      closeLabel="Remove chat filter"
                      onClose={library.clearChatFilter}
                    />
                  )}
                  {state.entryFilter && (
                    <FilterPill
                      block={block}
                      label={`Entry: ${state.entryFilter.text || 'Untitled message'}`}
                      title={state.entryFilter.text}
                      closeLabel="Remove entry filter"
                      onClose={library.clearEntryFilter}
                    />
                  )}
                </FlexLayout>
              </div>
            )}

          </div>

          <div ref={library.listRef} className={`${block}__list u-els-margin-top-2x`}>
            {renderResults()}
          </div>

          {renderPaginator()}
        </div>
      </div>
    </div>
  );
};
